/**
 * Input Sanitization Utilities
 * Use these on all user inputs before saving to database
 */

// Remove potential XSS/injection characters, trim, limit length
export function sanitizeText(input, maxLength = 500) {
  if (!input || typeof input !== 'string') return '';
  return input
    .trim()
    .replace(/[<>{}]/g, '')        // Remove HTML/JS brackets
    .replace(/javascript:/gi, '')   // Remove JS protocol
    .replace(/on\w+=/gi, '')        // Remove event handlers
    .slice(0, maxLength);
}

// Sanitize name (letters, spaces, hyphens, apostrophes only)
export function sanitizeName(input) {
  if (!input || typeof input !== 'string') return '';
  return input
    .trim()
    .replace(/[^a-zA-Z\s\-'À-ÿ]/g, '')  // Allow letters, spaces, hyphens, apostrophes, accents
    .slice(0, 100);
}

// Sanitize phone (digits, plus, parens, hyphens, spaces only)
export function sanitizePhone(input) {
  if (!input || typeof input !== 'string') return '';
  return input
    .replace(/[^\d+().\-\s]/g, '')
    .slice(0, 20);
}

// Sanitize email
export function sanitizeEmail(input) {
  if (!input || typeof input !== 'string') return '';
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9@._\-+]/g, '')
    .slice(0, 254);
}

// Sanitize address (alphanumeric + common address chars)
export function sanitizeAddress(input) {
  if (!input || typeof input !== 'string') return '';
  return input
    .trim()
    .replace(/[<>{}]/g, '')
    .slice(0, 500);
}

// Sanitize SKU (alphanumeric, hyphens, underscores)
export function sanitizeSku(input) {
  if (!input || typeof input !== 'string') return '';
  return input
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9\-_]/g, '')
    .slice(0, 50);
}

// Sanitize currency input
export function sanitizePrice(input) {
  if (typeof input === 'number') return Math.max(0, input);
  if (!input || typeof input !== 'string') return 0;
  const cleaned = input.replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : Math.max(0, num);
}

// Sanitize URL - blocks javascript: and other dangerous protocols
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  
  // Block dangerous protocols
  const lower = trimmed.toLowerCase().replace(/\s/g, '');
  if (lower.startsWith('javascript:') || 
      lower.startsWith('data:') || 
      lower.startsWith('vbscript:') ||
      lower.startsWith('file:')) {
    return '';
  }
  
  return trimmed;
}

// Sanitize integer
export function sanitizeInteger(input, min = 0, max = 9999) {
  if (typeof input === 'number') return Math.min(max, Math.max(min, Math.floor(input)));
  if (!input) return min;
  const num = parseInt(input, 10);
  return isNaN(num) ? min : Math.min(max, Math.max(min, num));
}

// Batch sanitize receipt form data
export function sanitizeReceiptData(formData) {
  return {
    ...formData,
    customerName: sanitizeName(formData.customerName),
    customerPhone: sanitizePhone(formData.customerPhone),
    customerEmail: sanitizeEmail(formData.customerEmail),
    deliveryAddress: sanitizeAddress(formData.deliveryAddress),
    internalNotes: sanitizeText(formData.internalNotes, 2000),
    amountPaid: sanitizePrice(formData.amountPaid),
    items: (formData.items || []).map(item => ({
      ...item,
      name: sanitizeText(item.name, 200),
      sku: sanitizeSku(item.sku),
      color: sanitizeText(item.color, 50),
      quantity: sanitizeInteger(item.quantity, 1, 999),
      price: sanitizePrice(item.price)
    })),
    customerPreferences: formData.customerPreferences ? {
      ...formData.customerPreferences,
      notes: sanitizeText(formData.customerPreferences.notes, 1000),
      preferredDeliveryDay: sanitizeText(formData.customerPreferences.preferredDeliveryDay, 50),
      preferredTimeWindow: sanitizeText(formData.customerPreferences.preferredTimeWindow, 50)
    } : null
  };
}